import { useEffect, useMemo, useRef, useState } from 'react';
import {
  saveReviewTaskResult,
  saveTaskDraft,
  subscribeToTask,
} from '../../services/tasks';
import { campaignRepository } from '../../platform/firebase/campaignRepository';
import {
  DraftSaveContext,
  TaskSessionActor,
  TaskSessionController,
  TaskSessionPorts,
} from './taskSessionController';

export interface UseTaskSessionControllerOptions {
  taskId: string | undefined;
  actor: TaskSessionActor | null;
  emptyResultForTask: (task: import('../../types').Task) => unknown;
  ports?: TaskSessionPorts;
}

function asFirebaseUser(actor: TaskSessionActor): Parameters<typeof saveTaskDraft>[1] {
  return {
    uid: actor.uid,
    email: actor.email ?? null,
    displayName: actor.displayName ?? null,
  } as Parameters<typeof saveTaskDraft>[1];
}

const defaultPorts: TaskSessionPorts = {
  subscribeTask: subscribeToTask,
  subscribeCampaign: campaignRepository.subscribeOne,
  saveTaskDraft: (taskId, actor, result) => saveTaskDraft(taskId, asFirebaseUser(actor), result),
  saveReviewTaskResult,
};

export function useTaskSessionController({
  taskId,
  actor,
  emptyResultForTask,
  ports = defaultPorts,
}: UseTaskSessionControllerOptions) {
  const controllerRef = useRef<TaskSessionController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new TaskSessionController({ ports, emptyResultForTask });
  }
  const controller = controllerRef.current;
  const [, setVersion] = useState(0);

  useEffect(() => controller.subscribe(() => setVersion((version) => version + 1)), [controller]);

  useEffect(() => {
    controller.updateContext({ taskId, actor, emptyResultForTask });
  }, [actor, controller, emptyResultForTask, taskId]);

  useEffect(() => () => {
    void controller.dispose();
  }, [controller]);

  const snapshot = controller.getSnapshot();
  return useMemo(() => ({
    controller,
    ...snapshot,
    initialize: () => controller.initialize(),
    dispose: () => controller.dispose(),
    setDraftResult: (next: unknown, context: DraftSaveContext) => controller.setDraftResult(next, context),
    saveDraft: (context: DraftSaveContext) => controller.saveDraft(context),
    flushForReload: (context: DraftSaveContext) => controller.flushForReload(context),
    cancelAutosave: () => controller.cancelAutosave(),
    discardPendingAutosave: () => controller.discardPendingAutosave(),
    applyTaskSnapshot: (task: import('../../types').Task | null) => controller.applyTaskSnapshot(task),
  }), [controller, snapshot]);
}
